import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@mui/material';

import Table from '../../../../../components/v2/Table';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import { ReactComponent as ArrowRightIcon } from '../../../../../assets/icons/v2/arrow-right.svg';
import { EM_DASH } from '../../utils/constants';
import texts from '../../texts.json';

import CampaignDetailsModal from '../CampaignDetailsModal';

const CampaignTable = ({
  docs,
  columns,
  loading,
  error,
  coachesList,
  userNames,
  noticeText,
  emptyText,
  errorText,
}) => {
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  const handleView = useCallback((id) => setSelectedCampaignId(id), []);
  const handleClose = useCallback(() => setSelectedCampaignId(null), []);

  const allColumns = useMemo(() => [
    ...columns,
    {
      field: 'view',
      flex: 0.6,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <PrimaryButton
          onClick={() => handleView(row.id)}
          endIcon={<ArrowRightIcon />}
          variant="info"
          size="medium"
        >
          {texts.history.viewAction}
        </PrimaryButton>
      ),
    },
  ], [columns, handleView]);

  const selected = useMemo(() => {
    if (!selectedCampaignId) return null;
    const doc = docs.find((d) => d.id === selectedCampaignId);
    if (!doc) return null;
    return {
      id: doc.id,
      data: doc.data,
      coachName: coachesList[doc.coachId] || EM_DASH,
      sentByName: userNames[doc.createdBy] || EM_DASH,
    };
  }, [docs, coachesList, userNames, selectedCampaignId]);

  const isEmpty = !loading && !error && docs.length === 0;

  return (
    <>
      {error && <Alert severity="error">{errorText}</Alert>}
      {!error && (
        <>
          {noticeText && (
            <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
              {noticeText}
            </Alert>
          )}
          {isEmpty
            ? <Alert severity="info">{emptyText}</Alert>
            : (
              <Table
                rows={docs}
                columns={allColumns}
                loading={loading}
                enableSearch={false}
                disableRowSelectionOnClick
              />
            )}
        </>
      )}
      <CampaignDetailsModal selected={selected} onClose={handleClose} />
      <LoadingOverlay isLoading={loading} />
    </>
  );
};

CampaignTable.propTypes = {
  docs: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  coachesList: PropTypes.object,
  userNames: PropTypes.object,
  noticeText: PropTypes.string,
  emptyText: PropTypes.string,
  errorText: PropTypes.string,
};

CampaignTable.defaultProps = {
  loading: false,
  error: false,
  coachesList: {},
  userNames: {},
  noticeText: '',
  emptyText: '',
  errorText: '',
};

export default CampaignTable;
